import { Award, Factory, ShieldCheck, Workflow } from "lucide-react";
import SEO from "../components/site/SEO";
import {
  CTASection,
  HighlightList,
  InfoCard,
  PageHero,
  SectionHeader,
} from "../components/site/Sections";
import {
  aboutHighlights,
  companyInfo,
  imageLibrary,
} from "../content/siteContent";

const commitments = [
  {
    title: "Experiência de campo",
    description:
      "Vivência prática em subestações, painéis, proteção e ativos elétricos que exigem precisão e leitura rápida de cenário.",
    icon: Award,
  },
  {
    title: "Execução segura",
    description:
      "Planejamento, metodologia e conformidade orientam cada intervenção para proteger pessoas, ativos e operação.",
    icon: ShieldCheck,
  },
  {
    title: "Entrega orientada à operação",
    description:
      "Relatórios claros, recomendações objetivas e suporte técnico alinhado às prioridades do cliente final.",
    icon: Workflow,
  },
  {
    title: "Atuação industrial e corporativa",
    description:
      "Conhecimento aplicado em ambientes industriais, condomínios, centros empresariais e facilities de alta responsabilidade.",
    icon: Factory,
  },
];

const AboutPage = () => (
  <>
    <SEO
      title="Sobre a W-Electric Engenharia Elétrica | Experiência e Autoridade Técnica"
      description="Conheça a experiência da W-Electric em subestações elétricas, ensaios em equipamentos, comissionamento de sistemas e projetos elétricos industriais."
      canonicalPath="/sobre"
      keywords={[
        "sobre empresa de engenharia elétrica",
        "subestações elétricas",
        "comissionamento de sistemas",
        "projetos elétricos industriais",
      ]}
    />

    <PageHero
      eyebrow="Sobre a empresa"
      title="Autoridade técnica para sistemas elétricos industriais e corporativos"
      description={`${companyInfo.shortName} atua com visão de engenharia, disciplina operacional e foco em gerar segurança para clientes que precisam de decisões técnicas bem fundamentadas.`}
      image={imageLibrary.industrial}
      ctaMessage="Olá, gostaria de conhecer melhor a W-Electric e solicitar atendimento."
      testId="about-hero"
    />

    <section className="container-shell py-14 md:py-20" data-testid="about-authority-section">
      <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
        <InfoCard
          title="Quem é a W-Electric"
          description="Somos uma empresa de engenharia elétrica com atuação voltada à confiabilidade de subestações, ensaios em equipamentos, comissionamento de sistemas e projetos elétricos industriais. Trabalhamos para que o cliente tenha clareza técnica, redução de risco e segurança operacional em cada entrega."
          items={aboutHighlights}
          testId="about-company-card"
          dark
        />

        <div className="space-y-8">
          <SectionHeader
            eyebrow="Posicionamento institucional"
            title="Presença corporativa, execução técnica e comunicação objetiva"
            description="Nossa proposta é entregar um atendimento de alto nível, com postura consultiva, evidências técnicas e visão prática para ambientes que exigem responsabilidade elétrica real."
            testId="about-authority-header"
          />
          <HighlightList items={aboutHighlights} testId="about-highlights-list" />
        </div>
      </div>
    </section>

    <section className="container-shell py-4 md:py-8" data-testid="about-commitments-section">
      <SectionHeader
        eyebrow="Compromissos"
        title="Base técnica que sustenta nossa entrega"
        description="Cada projeto ou manutenção é conduzido com foco em resultado, previsibilidade e proteção da operação do cliente."
        testId="about-commitments-header"
      />

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        {commitments.map((item, index) => {
          const Icon = item.icon;

          return (
            <div className="surface-card p-6 md:p-8" key={item.title} data-testid={`about-commitment-card-${index + 1}`}>
              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center bg-brand text-safety" data-testid={`about-commitment-icon-${index + 1}`}>
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-3xl uppercase text-brand" data-testid={`about-commitment-title-${index + 1}`}>
                {item.title}
              </h3>
              <p className="mt-4 body-copy" data-testid={`about-commitment-description-${index + 1}`}>
                {item.description}
              </p>
            </div>
          );
        })}
      </div>
    </section>

    <CTASection
      title="Quer trabalhar com uma equipe técnica de confiança?"
      description="Entre em contato com a W-Electric para avaliar necessidades de manutenção, ensaios, comissionamento ou adequações elétricas em São Paulo."
      buttonLabel="Solicitar orçamento"
      buttonMessage="Olá, gostaria de falar com a W-Electric sobre um projeto elétrico."
      testId="about-cta"
    />
  </>
);

export default AboutPage;